import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { Autocomplete, CircularProgress, FormControl, Grid, TextField, Theme } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { UpdateBusinessEntityUsersRequestDto } from '@sparling/financial-common'
import { classValidatorResolver } from '@hookform/resolvers/class-validator'
import { makeStyles } from '@mui/styles'
import { getUsersForAssignEntityRequest, updateBusinessEntityUsersRequest } from '../../../../store/actions'
import { getUsersForAssignEntityData, usersForAssignEntityLoading } from '../../../../store/selectors'
import { Modal } from '../../../../components/Modal'

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  inputWrap: {
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  noOptions: {
    color: theme.palette.primary.light,
    padding: theme.spacing(2),
  },
  chip: {
    color: theme.palette.primary.light,
  },
}))

interface UpdateBusinessEntityUsersModalProps {
  id: string
  isOpen: boolean
  modalClose: () => void
}

export const UpdateBusinessEntityUsersModal: FC<UpdateBusinessEntityUsersModalProps> = ({ id, isOpen, modalClose }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const usersForAssignEntityIsLoading = useSelector(usersForAssignEntityLoading)
  const { items } = useSelector(getUsersForAssignEntityData)

  const [search, setSearch] = useState<string>('')

  const formMethods = useForm<UpdateBusinessEntityUsersRequestDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    shouldFocusError: true,
    resolver: classValidatorResolver(UpdateBusinessEntityUsersRequestDto),
  })
  const {
    handleSubmit,
    getValues,
    control,
    formState: { isValid },
  } = formMethods

  const onSubmit = useCallback(() => {
    dispatch(updateBusinessEntityUsersRequest({ id, ...getValues() }))
    modalClose()
  }, [dispatch, id, getValues, modalClose])

  useEffect(() => {
    dispatch(getUsersForAssignEntityRequest({ ...(search.length > 2 && { search }) }))
  }, [dispatch, search])

  const selectedUserId = useMemo(() => getValues().users?.[0] ?? null, [getValues])

  return (
    <Modal
      title={'Update Business Entity Users'}
      isOpen={isOpen}
      modalClose={modalClose}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitDisabled={!isValid}
      width={600}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container className={classes.container}>
            <Grid item container className={classes.inputWrap}>
              <FormControl style={{ width: 500 }}>
                <Controller
                  name="users"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={items}
                      noOptionsText={'Entities not found'}
                      classes={{
                        noOptions: classes.noOptions,
                      }}
                      getOptionLabel={(option) => `${option.idSf} ${option.name}`}
                      loading={usersForAssignEntityIsLoading}
                      value={items.find((u) => u.id === selectedUserId) || null}
                      inputValue={search}
                      onInputChange={(_, value, reason) => {
                        if (reason === 'input') {
                          setSearch(value)
                        }
                      }}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          field.onChange([newValue.id])
                          setSearch(`${newValue.idSf} ${newValue.name}`)
                        } else {
                          field.onChange([])
                          setSearch('')
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {usersForAssignEntityIsLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  )}
                />
              </FormControl>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </Modal>
  )
}
